import PropTypes from 'prop-types'
import React, { forwardRef, useCallback } from 'react'
import DropdownList from './DropdownList'
import useFilteredDropdownList from './useFilteredDropdownList'

/**
 * Create a Filtered Dropdown List component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').FilteredDropdownListProps<T, Item>} props
 * @param {React.Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function filteredDropdownList (
  { onItemClick, ...props },
  ref
) {
  /** @type {(indices: number[], data: Item, event: React.SyntheticEvent)} */
  const handleSelectedItemsChanged = useCallback(
    (indices, data, event) => {
      onItemClick(data, indices.length ? indices[0] : -1, event)
    },
    [onItemClick]
  )
  const {
    selectedItemsIndices,
    onSelectedItemsChanged,
    ...filteredProps
  } = useFilteredDropdownList(
    {
      ...props,
      onSelectedItemsChanged: handleSelectedItemsChanged
    }
  )
  /** @type {(data: {value: Item}, index: number, event: React.SyntheticEvent) => void} */
  const handleItemClick = useCallback(
    (data, index, event) => {
      onSelectedItemsChanged(index === -1 ? [] : [index], data.value, event)
    },
    [onSelectedItemsChanged]
  )

  return (
    <DropdownList
      {...props}
      {...filteredProps}
      onItemClick={handleItemClick}
      ref={ref}
    />
  )
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').FilteredDropdownListProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const FilteredDropdownList = forwardRef(filteredDropdownList)
FilteredDropdownList.displayName = 'FilteredDropdownList'
FilteredDropdownList.props = {
  ...DropdownList.props,
  getItemValue: PropTypes.func,
  filter: PropTypes.string
}
FilteredDropdownList.defaultProps = {
  ...DropdownList.defaultProps,
  getItemValue: String,
  filter: ''
}

export default FilteredDropdownList
